import { MouseEvent, useState } from "react";
import "./hierarchy.scss";

import { Edit } from "@mui/icons-material";
import { Box, Typography } from "@mui/material";

import { ADMIN_PERMISSIONS } from "~/routes/components/ProtectedRoute/types";

import FacilityFormModal from "#/features/Hierarchy/components/FacilityFormModal";
import HealthSystemFormModal from "#/features/Hierarchy/components/HealthSystemFormModal";
import MarketFormModal from "#/features/Hierarchy/components/MarketFormModal";
import RegionFormModal from "#/features/Hierarchy/components/RegionFormModal";
import UnitFormModal from "#/features/Hierarchy/components/UnitFormModal";
import {
  HierarchyModelBase,
  HierarchyModelView,
  EHierarchyModelViewType,
} from "#/features/Hierarchy/types";
import { CustomButton } from "@/common/components";
import { useCheckUserPermissions } from "@/common/hooks";

import { HierarchyContainer } from "./components";
import { useHierarchyData } from "./hooks/useHierarchyData";

type SelectedItemProps = { selected: HierarchyModelBase; parentOptions?: HierarchyModelBase[] };

export const HierarchyPage = () => {
  const { top, onClick, onChange, isLoading } = useHierarchyData();
  const checkPermission = useCheckUserPermissions();
  const [system, setSystem] = useState<HierarchyModelBase | undefined>();
  const [region, setRegion] = useState<SelectedItemProps | undefined>();
  const [market, setMarket] = useState<SelectedItemProps | undefined>();
  const [facility, setFacility] = useState<SelectedItemProps | undefined>();
  const [unit, setUnit] = useState<SelectedItemProps | undefined>();
  const [editEnabled, setEditEnabled] = useState(false);
  const canView = checkPermission("read", ADMIN_PERMISSIONS);

  if (!canView) return <div>You do not have permission to view this page</div>;

  const onEdit = (
    event: MouseEvent<HTMLButtonElement>,
    view: HierarchyModelView,
    item?: HierarchyModelBase,
  ) => {
    event.stopPropagation();

    switch (view.type) {
      case EHierarchyModelViewType.healthSystem:
        setSystem(item ?? { id: "", name: "" });
        break;
      case EHierarchyModelViewType.region:
        setRegion({
          parentOptions: view.parentOptions,
          selected: item ?? { id: "", name: "", healthSystemId: view.model.id },
        });
        break;
      case EHierarchyModelViewType.market:
        setMarket({
          parentOptions: view.parentOptions,
          selected: item ?? { id: "", name: "", regionId: view.model.id },
        });
        break;
      case EHierarchyModelViewType.facility:
        setFacility({
          parentOptions: view.parentOptions,
          selected: item ?? { id: "", name: "", marketId: view.model.id },
        });
        break;
      case EHierarchyModelViewType.unit:
        setUnit({
          parentOptions: view.parentOptions,
          selected: item ?? {
            id: "",
            name: "",
            facilityId: view.model.id,
            scheduleDurationInWeeks: 1,
          },
        });
        break;
    }
  };

  return (
    <Box className="hierarchy-view-page-container">
      <Box className={"toolbar"}>
        <Typography variant="h5" children="Hierarchy Configuration" />

        <CustomButton
          trackingLabel={null}
          className={"enable-edit"}
          onClick={() => setEditEnabled(!editEnabled)}
          startIcon={<Edit />}
          variant="outlined"
        >
          <Typography>{editEnabled ? "Stop Editing" : "Edit"}</Typography>
        </CustomButton>
      </Box>

      <Typography variant="body2">
        View, edit or archive the hierarchy configuration of health systems and their regions,
        markets, facilities and units.
      </Typography>

      <HierarchyContainer
        isLoading={isLoading}
        top={top}
        onClick={onClick}
        onEdit={onEdit}
        editEnabled={editEnabled}
      />

      {/* Modals */}
      <HealthSystemFormModal
        selected={system}
        onComplete={(updated) => {
          setSystem(undefined);
          onChange(updated);
        }}
        data-testid="health-system-form-modal"
      />

      <RegionFormModal
        onComplete={(updated) => {
          setRegion(undefined);
          onChange(updated);
        }}
        {...region}
      />

      <MarketFormModal
        onComplete={(updated) => {
          setMarket(undefined);
          onChange(updated);
        }}
        {...market}
      />

      <FacilityFormModal
        onComplete={(updated) => {
          setFacility(undefined);
          onChange(updated);
        }}
        {...facility}
      />

      <UnitFormModal
        onComplete={(updated) => {
          setUnit(undefined);
          onChange(updated);
        }}
        {...unit}
      />
    </Box>
  );
};
